import type { Editor } from "@tiptap/react";

import { ensureHttpsProtocol } from "simplydo/core/linkHref";

export type MenuCommands = {
  // Keep this intentionally small and explicit.
  // It’s passed to `extraMenuItems` so they don’t need to know about chain/run.
  toggleBold?: () => void;
  toggleItalic?: () => void;
  toggleUnderline?: () => void;
  toggleStrike?: () => void;
  toggleHeading?: (opts: { level: number }) => void;
  toggleBulletList?: () => void;
  toggleOrderedList?: () => void;
  setAlignment?: (opts: { direction: "center" | "right" }) => void;
  removeAlignment?: () => void;

  undo?: () => void;
  redo?: () => void;

  insertText?: (text: string) => void;
  updateLink?: (
    attrs: { href: string },
    range?: { from: number; to: number },
  ) => void;
  removeLink?: () => void;

  // Used by preset menus (e.g. `getFullMenu`)
  createCustomLink?: (data: { text?: string; href: string }) => void;
  insertLink?: (data: { href: string }) => void;

  insertImage?: (data: { src: string }) => void;
  insertVideo?: (data: { src: string }) => void;
  addIframe?: (data: { src: string; title?: string; allow?: string }) => void;
  insertFile?: (data: { name: string; src: string }) => void;
  updateFile?: (attrs: { name?: string; src?: string | null }) => void;
  removeFile?: () => void;

  insertTable?: () => void;
  addColumnBefore?: () => void;
  addColumnAfter?: () => void;
  deleteColumn?: () => void;
  addRowBefore?: () => void;
  addRowAfter?: () => void;
  deleteRow?: () => void;
  deleteTable?: () => void;
  mergeCells?: () => void;
  splitCell?: () => void;
  toggleHeaderColumn?: () => void;
  toggleHeaderRow?: () => void;
  toggleHeaderCell?: () => void;
  mergeOrSplit?: () => void;
};

export const createMenuCommands = (editor: Editor | null): MenuCommands => {
  if (!editor) {
    return {};
  }

  const focus = () => editor.chain().focus();

  const linkifyText = (text: string, href: string) => {
    const normalisedHref = ensureHttpsProtocol(href);
    const { from } = editor.state.selection;
    focus()
      .insertContent(`${text} `)
      .setTextSelection({ from, to: from + text.length })
      .setLink({ href: normalisedHref })
      .setTextSelection(from + text.length + 1)
      .unsetMark("link")
      .run();
  };

  return {
    toggleBold: () => focus().toggleBold().run(),
    toggleItalic: () => focus().toggleItalic().run(),
    toggleUnderline: () => focus().toggleUnderline().run(),
    toggleStrike: () => focus().toggleStrike().run(),
    toggleHeading: ({ level }) =>
      focus()
        .toggleHeading({ level: level as 1 | 2 | 3 | 4 | 5 | 6 })
        .run(),
    toggleBulletList: () => focus().toggleBulletList().run(),
    toggleOrderedList: () => focus().toggleOrderedList().run(),
    setAlignment: (opts) => focus().setAlignment(opts).run(),
    removeAlignment: () => focus().removeAlignment().run(),

    undo: () => focus().undo().run(),
    redo: () => focus().redo().run(),

    insertText: (text) => focus().insertContent(text).run(),
    updateLink: (attrs, range) => {
      const href = ensureHttpsProtocol(attrs.href);
      if (range) {
        focus()
          .setTextSelection(range)
          .setLink({ ...attrs, href })
          .run();
        return;
      }
      focus()
        .extendMarkRange("link")
        .setLink({ ...attrs, href })
        .run();
    },
    removeLink: () => focus().extendMarkRange("link").unsetLink().run(),

    createCustomLink: ({ text, href }) => {
      const normalisedHref = ensureHttpsProtocol(href);
      const url = text || normalisedHref;
      const { from, to } = editor.state.selection;
      if (from === to) {
        linkifyText(url, normalisedHref);
        return;
      }
      focus().setLink({ href: normalisedHref }).run();
    },
    insertLink: ({ href }) => {
      const normalisedHref = ensureHttpsProtocol(href);
      linkifyText(normalisedHref, normalisedHref);
    },

    insertImage: (data) => focus().setImage(data).run(),
    insertVideo: (data) => focus().insertVideo(data).run(),
    addIframe: (data) => focus().addIframe(data).run(),
    insertFile: (data) => focus().insertFile(data).run(),
    updateFile: (attrs) => focus().updateFile(attrs).run(),
    removeFile: () => focus().removeFile().run(),

    insertTable: () => focus().insertTable().run(),
    addColumnBefore: () => focus().addColumnBefore().run(),
    addColumnAfter: () => focus().addColumnAfter().run(),
    deleteColumn: () => focus().deleteColumn().run(),
    addRowBefore: () => focus().addRowBefore().run(),
    addRowAfter: () => focus().addRowAfter().run(),
    deleteRow: () => focus().deleteRow().run(),
    deleteTable: () => focus().deleteTable().run(),
    mergeCells: () => focus().mergeCells().run(),
    splitCell: () => focus().splitCell().run(),
    toggleHeaderColumn: () => focus().toggleHeaderColumn().run(),
    toggleHeaderRow: () => focus().toggleHeaderRow().run(),
    toggleHeaderCell: () => focus().toggleHeaderCell().run(),
    mergeOrSplit: () => focus().mergeOrSplit().run(),
  };
};
